import { Button } from '@design/components/button/Button';
import { PasswordInput } from '@terminal/components/forms/password-input/PasswordInput';
import { LoadingView } from '@terminal/components/loading-view/LoadingView';
import { ToggleBrandLogo } from '@terminal/components/toggle-brand-logo/ToggleBrandLogo';
import { appPaths, authPaths } from '@terminal/routes/app-paths';
import {
  validateConfirmPassword,
  validatePassword,
} from '@terminal/utils/password-validation-utils/password-validation-utils';
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import authStyles from '../auth-shared-styles/AuthSharedStyles.module.scss';
import { useResetPasswordState } from './hooks/use-reset-password-state/useResetPasswordState';
import styles from './ResetPasswordView.module.scss';

enum ChangePasswordFields {
  NewPassword = 'newPassword',
  ConfirmedPassword = 'confirmedPassword',
}

const defaultErrorState = {
  newPassword: '',
  confirmedPassword: '',
};

export const ResetPasswordView = () => {
  const { t } = useTranslation(['auth', 'common', 'settings']);
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [errors, setErrors] = useState(defaultErrorState);
  const { resetState, resetPassword, checkToken, checkTokenState } =
    useResetPasswordState();

  useEffect(() => {
    checkToken();
  }, []);

  const error = useMemo(() => {
    return resetState.error || checkTokenState.error;
  }, [checkTokenState, resetState]);

  const updateError = (field: ChangePasswordFields, errorText: string) => {
    setErrors(prevState => ({
      ...prevState,
      [field]: errorText,
    }));
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === ChangePasswordFields.NewPassword) {
      setPassword(value);
    }

    if (name === ChangePasswordFields.ConfirmedPassword) {
      setConfirmedPassword(value);
    }
  };

  const clearError = (event: React.FocusEvent<HTMLInputElement>) => {
    updateError(event.target.name as ChangePasswordFields, '');
  };

  const validatePasswords = (
    newPassword: string,
    confirmedPassword: string
  ) => {
    const newPasswordError = validatePassword(newPassword);
    const confirmedPasswordError = validateConfirmPassword(
      confirmedPassword,
      newPassword
    );

    return { newPasswordError, confirmedPasswordError };
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    const { newPasswordError, confirmedPasswordError } = validatePasswords(
      password,
      confirmedPassword
    );

    setErrors({
      newPassword: newPasswordError,
      confirmedPassword: confirmedPasswordError,
    });

    const isFormValid = !newPasswordError && !confirmedPasswordError;

    if (isFormValid) {
      resetPasswordHandler();
    }
  };

  const resetPasswordHandler = useCallback(async () => {
    if (resetState.isPending) {
      return null;
    }

    return resetPassword({
      password,
      confirmedPassword,
    });
  }, [password, confirmedPassword]);

  return (
    <div
      className={authStyles.layout}
      style={{ backgroundImage: `url('/app/static/images/auth/sun.png')` }}
    >
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <LoadingView loading={!(checkTokenState.isSuccess || error)}>
            <ToggleBrandLogo
              className={authStyles.logo}
              alt="logo"
              title="logo"
            />
            <div className={styles.errorContainer}>
              {error && (
                <div className={styles.noLongerValidWrapper}>
                  <section className={styles.errorView} data-testid="errorView">
                    <h3 className={styles.errorTitle}>
                      {
                        /* i18next-extract-disable-next-line */
                        t(`auth:resetPassword.${error}`)
                      }
                    </h3>
                    <Link to={authPaths.forgotPassword}>
                      <Button
                        fullWidth
                        label={t('auth:resetPassword.linkToForgotPassword')}
                        size="large"
                      />
                    </Link>
                    <Link
                      className={styles.backToLoginLink}
                      to={{ pathname: authPaths.login }}
                    >
                      <Link
                        to={authPaths.login}
                        className={authStyles.cancelButton}
                      >
                        {t('auth:resetPassword.backToLogin')}
                      </Link>
                    </Link>
                  </section>
                </div>
              )}

              {checkTokenState.isSuccess && !error && (
                <form
                  className={styles.form}
                  data-testid="resetPasswordForm"
                  onSubmit={onSubmit}
                >
                  <h1 className={authStyles.heading}>
                    {t('auth:resetPassword.title')}
                  </h1>

                  <PasswordInput
                    className={styles.styledPasswordInput}
                    data-testid="password-input"
                    label={t('settings:account.newPassword')}
                    variant="outline"
                    name={ChangePasswordFields.NewPassword}
                    onChange={onInputChange}
                    onFocus={clearError}
                    hasError={!!errors.newPassword}
                    /* i18next-extract-disable-next-line */
                    errorText={t(errors.newPassword)}
                  />
                  <PasswordInput
                    data-testid="confirm-password-input"
                    label={t('settings:account.confirmPassword')}
                    name={ChangePasswordFields.ConfirmedPassword}
                    onChange={onInputChange}
                    onFocus={clearError}
                    hasError={!!errors.confirmedPassword}
                    /* i18next-extract-disable-next-line */
                    errorText={t(errors.confirmedPassword)}
                  />
                  <div className={authStyles.centerContent}>
                    <div className={authStyles.sendButtonWrapper}>
                      <Button
                        fullWidth
                        data-testid="submit-button"
                        disabled={resetState.isPending}
                        isLoading={resetState.isPending}
                        label={t('auth:resetPassword.update')}
                        size="large"
                      />
                    </div>
                    <a href={appPaths.base} className={authStyles.cancelButton}>
                      {t('common:cancel')}
                    </a>
                  </div>
                </form>
              )}
            </div>
          </LoadingView>
        </div>
      </div>
    </div>
  );
};
